import { useEffect, useState, useCallback } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  TextField, Box, Button, FormControl, InputLabel, 
  Select, MenuItem, Grid, Typography, Alert,
  FormHelperText, FormControlLabel, Checkbox
} from '@mui/material';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import DropZone from '../common/DropZone';
import { generateSlug } from '../../utils/slugGenerator';
import { uploadSingleImage } from '../../services/upload.service';

// Slug removed from schema — auto-generated
const clergySchema = z.object({
  slug: z.string().optional(), // Hidden, auto-generated
  name: z.string().min(1, 'Укажите имя'),
  // В базе саны хранятся по-русски («Протоиерей», «Иерей», …) — так же их читает
  // сортировка по старшинству на публичной странице. Раньше здесь стоял enum
  // BISHOP/PRIEST/DEACON/MONK: у существующей записи сан не проходил валидацию,
  // и отредактировать её было нельзя вовсе.
  rank: z.string().min(1, 'Укажите сан'),
  position: z.string().optional(),
  sortOrder: z.coerce.number().int().min(0).optional(),
  biography: z.string().optional(),
  education: z.string().optional(),
  awards: z.string().optional(),
  photoUrl: z.string().optional(), // Uploaded via DropZone
  email: z.string().email('Некорректный email').optional().or(z.literal('')),
  phone: z.string().optional(),
  isActive: z.boolean().default(true),
});

type ClergyFormValues = z.infer<typeof clergySchema>;

interface ClergyFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ClergyFormValues) => Promise<void>;
  initialData?: ClergyFormValues | null;
  loading: boolean;
  error?: string | null;
}

export function ClergyForm({ open, onClose, onSubmit, initialData, loading, error }: ClergyFormProps) {
  const [photoUploading, setPhotoUploading] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<ClergyFormValues>({
    resolver: zodResolver(clergySchema),
    defaultValues: {
      slug: '',
      name: '',
      rank: 'Иерей',
      position: '',
      sortOrder: 0,
      biography: '',
      education: '',
      awards: '',
      photoUrl: '',
      email: '',
      phone: '',
      isActive: true,
    },
  });

  const nameValue = watch('name');
  const photoUrl = watch('photoUrl');

  // Auto-generate slug when name changes (only for new items)
  useEffect(() => {
    if (!initialData && nameValue && nameValue.trim().length > 0) {
      const autoSlug = generateSlug(nameValue);
      setValue('slug', autoSlug);
    }
  }, [nameValue, initialData, setValue]);

  useEffect(() => {
    if (initialData) {
      reset(initialData);
    } else {
      reset({
        slug: '',
        name: '',
        rank: 'Иерей',
        position: '',
        sortOrder: 0,
        biography: '',
        education: '',
        awards: '',
        photoUrl: '',
        email: '',
        phone: '',
        isActive: true,
      });
    }
  }, [initialData, open, reset]);

  // Handle image upload via DropZone
  const handleImageUpload = useCallback(async (files: File[]): Promise<{ url: string }[]> => {
    setPhotoUploading(true);
    try {
      const results: { url: string }[] = [];
      for (const file of files) {
        const url = await uploadSingleImage(file);
        results.push({ url });
      }
      return results;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка загрузки';
      throw new Error(message);
    } finally {
      setPhotoUploading(false);
    }
  }, []);

  const handlePhotoChange = useCallback((urls: string | string[]) => {
    const url = Array.isArray(urls) ? urls[0] : urls;
    setValue('photoUrl', url);
  }, [setValue]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {initialData ? 'Редактировать священнослужителя' : 'Добавить священнослужителя'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {/* Name — main field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="ФИО *"
                error={!!errors.name}
                helperText={errors.name?.message}
                {...register('name')}
              />
            </Grid>
            
            {/* Slug — hidden field, shown as read-only hint */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="URL (автоматически)"
                value={watch('slug') || ''}
                disabled
                helperText="Генерируется из имени"
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    bgcolor: 'action.hover',
                  }
                }}
              />
            </Grid>
            
            {/* Rank */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.rank}>
                <InputLabel>Сан *</InputLabel>
                <Select
                  label="Сан *"
                  {...register('rank')}
                >
                  <MenuItem value="Архимандрит">Архимандрит</MenuItem>
                  <MenuItem value="Игумен">Игумен</MenuItem>
                  <MenuItem value="Иеромонах">Иеромонах</MenuItem>
                  <MenuItem value="Протоиерей">Протоиерей</MenuItem>
                  <MenuItem value="Иерей">Иерей</MenuItem>
                  <MenuItem value="Протодиакон">Протодиакон</MenuItem>
                  <MenuItem value="Диакон">Диакон</MenuItem>
                </Select>
                <FormHelperText>{errors.rank?.message}</FormHelperText>
              </FormControl>
            </Grid>
            
            {/* Position */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Должность"
                {...register('position')}
              />
            </Grid>

            {/* Порядок в списке — по нему строится старшинство на публичной странице */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Порядок в списке"
                helperText="Чем меньше число, тем выше в списке. Шаг 10 — между соседями можно вставить нового. 0 — в конец, по сану."
                {...register('sortOrder', { valueAsNumber: true })}
              />
            </Grid>

            {/* isActive checkbox */}
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox {...register('isActive')} defaultChecked />}
                label="Священнослужитель активен"
              />
            </Grid>
            
            {/* Photo — DropZone instead of URL TextField */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Фотография
              </Typography>
              <DropZone
                onUpload={handleImageUpload}
                type="image"
                maxFiles={1}
                maxSize={10}
                multiple={false}
                value={photoUrl || ''}
                onChange={handlePhotoChange}
                label="Перетащите изображение или нажмите для выбора"
                helperText="JPG, PNG, WebP до 10MB"
              />
              {photoUploading && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Загрузка...
                </Typography>
              )}
            </Grid>
            
            {/* Contact Info */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                error={!!errors.email}
                helperText={errors.email?.message}
                {...register('email')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Телефон"
                {...register('phone')}
              />
            </Grid>
            
            {/* Education */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Образование"
                multiline
                rows={2}
                {...register('education')}
              />
            </Grid>
            
            {/* Awards */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Награды"
                multiline
                rows={2}
                {...register('awards')}
              />
            </Grid>
            
            {/* Biography */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Биография"
                multiline
                rows={4}
                {...register('biography')}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                * Обязательные поля
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button onClick={handleSubmit(onSubmit)} variant="contained" disabled={loading || photoUploading}>
          {loading ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}